import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import { type ProspectContactCrmStatus, type ProspectListCrmStatus, api } from "../../../api/client.ts";
import { keys } from "../../../api/queryKeys.ts";

export interface CrmStatusResult {
  /** Keyed by prospect list contact entry id. */
  byEntry: Map<string, ProspectContactCrmStatus>;
  summary: ProspectListCrmStatus["summary"] | null;
  /** The CRM connection this list exports into, once resolved. */
  connectionId: string | null;
  /**
   * False when this list's tenant has no CRM connection (or several, needing an
   * explicit choice). Callers should render nothing rather than "not in CRM",
   * which would read as a fact about the contact rather than about the setup.
   */
  available: boolean;
}

/**
 * CRM sequence + deal state for a list's contacts.
 *
 * Gated behind `enabled` for the same reason the contacts query is: the default
 * companies view should stay a single request.
 */
export function useCrmStatus(listId: string, enabled: boolean): CrmStatusResult {
  const query = useQuery({
    queryKey: keys.prospectListCrmStatus.byList(listId),
    queryFn: () => api.getProspectListCrmStatus(listId),
    enabled: enabled && !!listId,
    // A tenant with no CRM connection is a configuration state, not a transient
    // failure — retrying just delays the empty render.
    retry: false,
    // ...and it's not a user-facing error either: `available` below renders
    // nothing on error, so suppress the global error toast.
    meta: { suppressErrorToast: true },
  });

  return useMemo(() => {
    const byEntry = new Map<string, ProspectContactCrmStatus>();
    for (const contact of query.data?.contacts ?? []) {
      byEntry.set(contact.contact_entry_id, contact);
    }
    return {
      byEntry,
      summary: query.data?.summary ?? null,
      connectionId: query.data?.connection_id ?? null,
      available: !query.isError && !!query.data,
    };
  }, [query.data, query.isError]);
}
